import React from 'react'
import EOpticAndCameraFeature from '../../Model/EOpticAndCameraFeature'
import {
  FluorescenceCaptureProperty,
  UltravioletFluorescenceCaptureProperty,
  VisibleFluorescenceCaptureProperty,
} from '../../Model/CaptureProfile'
import {
  EFilterCube,
  LightPathCapability,
} from '../../Model/ImagerCapabilities'
import { Fov, ImagerState } from '../../Model/ModuleState'
import { BinningControl } from './Control/BinningControl'
import { BrightnessControl } from './Control/BrightnessControl'
import { ExposureControl } from './Control/ExposureControl'
import { GainControl } from './Control/GainControl'
import { GammaControl } from './Control/GammaControl'
import { IlluminationControl } from './Control/IlluminationControl'
import { ImagerInfo } from './Control/ImagerInfoControl'
import { ObjectiveZoomControl } from './Control/ObjectiveZoomControl'
import { BasicSelectionControl } from './ImagerFeatureControls'
import FovControl from './Control/FovControl'
import AutoExposureControl from './Control/AutoExposureControl'
import { CropZoomControl } from './Control/CropZoomControl'

function getIsVisibleFluorescence(
  lightPathCapability: LightPathCapability,
  filterCubeId: number,
) {
  const filterCubes = lightPathCapability.optic.filterCubes
  if (filterCubes == null) {
    throw new Error('Filter cubes is null')
  }
  if (filterCubeId === -1) filterCubeId = 0
  const selectedFilterCube = filterCubes.filterCubes.filter(
    (fc) => fc.id === filterCubeId,
  )[0]
  return selectedFilterCube.type === EFilterCube.VisibleFluorescence
}

type Props = {
  disabled: boolean
  lightPathCapability: LightPathCapability
  fluorescenceCaptureProperty: FluorescenceCaptureProperty
  onFluorescenceCapturePropertyChanged: (
    fluorescenceCaptureProperty: FluorescenceCaptureProperty,
  ) => void
  onAutoExposure: () => void
  fov: Fov
  imagerState: ImagerState
  style?: React.CSSProperties
}

export default function FluorescenceOpticAndCameraControl({
  disabled,
  lightPathCapability,
  fluorescenceCaptureProperty,
  onAutoExposure,
  fov,
  onFluorescenceCapturePropertyChanged,
  imagerState,
}: Props) {
  const visFCp = fluorescenceCaptureProperty.visibleFluorescence
  const uvFCp = fluorescenceCaptureProperty.ultravioletFluorescence

  if (visFCp == null) {
    throw new Error('Visible fluorescence capture profile is null')
  }

  if (uvFCp == null) {
    throw new Error('Ultraviolet fluorescence capture profile is null')
  }

  if (fluorescenceCaptureProperty.filterCubeId == null) {
    throw new Error('Capture profile filter cube id is null')
  }

  const onVisibleFluorescenceCapturePropertyChanged = (
    visibleFluorescence: VisibleFluorescenceCaptureProperty,
  ): void => {
    onFluorescenceCapturePropertyChanged({
      visibleFluorescence: visibleFluorescence,
    })
  }

  const onUltravioletFluorescenceCapturePropertyChanged = (
    ultravioletFluorescence: UltravioletFluorescenceCaptureProperty,
  ): void => {
    onFluorescenceCapturePropertyChanged({
      ultravioletFluorescence: ultravioletFluorescence,
    })
  }

  const isVisibleFluorescence = getIsVisibleFluorescence(
    lightPathCapability,
    fluorescenceCaptureProperty.filterCubeId,
  )

  let opticAndCameraControls = []

  //-------OPTIC------//

  const objectiveZoom = lightPathCapability.optic.objectiveZoom
  const brightness = lightPathCapability.optic.brightness
  const filterCubes = lightPathCapability.optic.filterCubes
  const cropZoom = lightPathCapability.optic.cropZoom

  //-------Camera------//

  const exposure = lightPathCapability.camera.exposure
  const gamma = lightPathCapability.camera.gamma
  const gain = lightPathCapability.camera.gain
  const binning = lightPathCapability.camera.binning

  if (filterCubes) {
    const displayValues = filterCubes.filterCubes.map((filterCube) => ({
      display: filterCube.name,
      value: filterCube.id.toString(),
    }))

    opticAndCameraControls.push(
      <BasicSelectionControl
        disabled={disabled}
        feature="Excitation"
        id={EOpticAndCameraFeature.FilterCube}
        value={fluorescenceCaptureProperty.filterCubeId.toString()}
        displayValues={displayValues}
        onValueChanged={(id, value) =>
          onFluorescenceCapturePropertyChanged({
            filterCubeId: Number.parseFloat(value),
          })
        }
      />,
    )
  }

  if (objectiveZoom) {
    opticAndCameraControls.push(
      <ObjectiveZoomControl
        disabled={disabled}
        objectiveZoom={objectiveZoom}
        captureProperty={fluorescenceCaptureProperty}
        onChange={onFluorescenceCapturePropertyChanged}
      />,
    )
  }
  if (cropZoom) {
    if (fluorescenceCaptureProperty.cropZoom == null) {
      throw new Error('Capture profile crop zoom is null')
    }
    opticAndCameraControls.push(
      <CropZoomControl
        cropZoom={cropZoom}
        disabled={disabled}
        captureProperty={fluorescenceCaptureProperty}
        onChange={onFluorescenceCapturePropertyChanged}
      />,
    )
  }

  let zoom = 1
  let maxZoom = 1

  if (
    fluorescenceCaptureProperty.objectiveZoom != null &&
    objectiveZoom != null
  ) {
    zoom = fluorescenceCaptureProperty.objectiveZoom
    maxZoom =
      objectiveZoom.objectives[objectiveZoom.objectives.length - 1].value
  }

  const maxFov: Fov = {
    width: fov.width * zoom,
    height: fov.height * zoom,
  }

  const minFov: Fov = {
    width: maxFov.width / maxZoom,
    height: maxFov.height / maxZoom,
  }

  opticAndCameraControls.push(
    <FovControl
      id="Fov"
      fov={fov}
      maxFov={maxFov}
      minFov={minFov}
      onValueChanged={(id, fov) => {}}
      disabled={true}
    />,
  )

  if (!isVisibleFluorescence) {
    opticAndCameraControls.push(
      <IlluminationControl
        disabled={disabled}
        captureProperty={uvFCp}
        onChange={onUltravioletFluorescenceCapturePropertyChanged}
      />,
    )
  }

  if (gamma) {
    opticAndCameraControls.push(
      <GammaControl
        disabled={disabled}
        gamma={gamma}
        captureProperty={isVisibleFluorescence ? visFCp : uvFCp}
        onChange={
          isVisibleFluorescence
            ? onVisibleFluorescenceCapturePropertyChanged
            : onUltravioletFluorescenceCapturePropertyChanged
        }
      />,
    )
  }

  if (
    (onAutoExposure !== null || onAutoExposure !== undefined) &&
    isVisibleFluorescence &&
    lightPathCapability.camera.autoExposure?.enable
  ) {
    opticAndCameraControls.push(
      <AutoExposureControl
        key={`optic-camera-control-autoexposure`}
        disabled={disabled}
        onAutoExposure={onAutoExposure}
      />,
    )
  }

  if (gain) {
    opticAndCameraControls.push(
      <GainControl
        disabled={disabled}
        gain={gain}
        captureProperty={isVisibleFluorescence ? visFCp : uvFCp}
        onChange={
          isVisibleFluorescence
            ? onVisibleFluorescenceCapturePropertyChanged
            : onUltravioletFluorescenceCapturePropertyChanged
        }
      />,
    )
  }

  if (exposure) {
    opticAndCameraControls.push(
      <ExposureControl
        disabled={disabled}
        exposure={exposure}
        captureProperty={isVisibleFluorescence ? visFCp : uvFCp}
        onChange={
          isVisibleFluorescence
            ? onVisibleFluorescenceCapturePropertyChanged
            : onUltravioletFluorescenceCapturePropertyChanged
        }
      />,
    )
  }

  if (brightness && isVisibleFluorescence) {
    opticAndCameraControls.push(
      <BrightnessControl
        disabled={disabled}
        brightness={brightness}
        captureProperty={isVisibleFluorescence ? visFCp : uvFCp}
        onChange={onVisibleFluorescenceCapturePropertyChanged}
      />,
    )
  }

  if (binning) {
    opticAndCameraControls.push(
      <BinningControl
        disabled={disabled}
        binning={binning}
        captureProperty={fluorescenceCaptureProperty}
        onChange={onFluorescenceCapturePropertyChanged}
      />,
    )
  }

  if (visFCp.exposure == null || uvFCp.exposure == null) {
    throw new Error('Capture profile exposure is null')
  }

  opticAndCameraControls.push(
    <ImagerInfo
      disabled={disabled}
      imagerState={imagerState}
      captureProperty={isVisibleFluorescence ? visFCp : uvFCp}
    />,
  )

  return <>{opticAndCameraControls}</>
}
